import React, { useEffect, useRef, useState } from "react";
import { UI_FONT_FAMILY } from "./fonts";
import { TEXT_LINE_HEIGHT, placeAtVisible, rotateVec, totalRotation, visibleSize, visibleToNative, visibleVecToNative } from "./geometry";
import { useInView, usePageRender } from "./usePageRender";

const SELECT_COLOR = "#0c76e5";
const DEFAULT_TEXT = "ข้อความ";

const pathOf = (points) => {
  if (!points.length) return "";
  if (points.length === 1) return `M ${points[0].x} ${points[0].y} L ${points[0].x + 0.01} ${points[0].y}`;
  return points.map((p, i) => `${i ? "L" : "M"} ${p.x} ${p.y}`).join(" ");
};

const bboxOf = (points, pad) => {
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const minX = Math.min(...xs) - pad;
  const minY = Math.min(...ys) - pad;
  return { x: minX, y: minY, w: Math.max(...xs) + pad - minX, h: Math.max(...ys) + pad - minY };
};

export { DEFAULT_TEXT };

export default function PageView({
  page, pdfDoc, zoom, tool, color, width, textSize, selectedIds = [], active, index,
  onActivate, onSelect, onBeginEdit, onUpdateMany, onAdd,
  // ใช้ซ้ำใน image-editor: bitmapSource = canvas ที่วาดแทนการเรนเดอร์ PDF, redact* = เครื่องมือปิดบังเฉพาะส่วน
  bitmapSource, hideLabel, redactMode, redactStrength,
}) {
  const hostRef = useRef(null);
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);
  const dragRef = useRef(null);
  const drawRef = useRef(null);
  const [draft, setDraft] = useState(null);

  const { W, H } = page;
  const R = totalRotation(page);
  const v = visibleSize(W, H, R);
  const z = zoom;
  const isSelectTool = tool === "select";

  const visible = useInView(hostRef);
  usePageRender(canvasRef, pdfDoc, page, z, visible);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!bitmapSource || !canvas) return;
    canvas.width = bitmapSource.width;
    canvas.height = bitmapSource.height;
    canvas.getContext("2d").drawImage(bitmapSource, 0, 0);
  }, [bitmapSource]);

  const visiblePoint = (event) => {
    const rect = wrapperRef.current.getBoundingClientRect();
    return { x: (event.clientX - rect.left) / z, y: (event.clientY - rect.top) / z };
  };

  /* ---------- วาดด้วยเครื่องมือ ---------- */

  const handlePageDown = (event) => {
    onActivate(page.id);
    if (event.button !== 0) return;
    if (isSelectTool) {
      if (!(event.shiftKey || event.metaKey || event.ctrlKey)) onSelect([]);
      return;
    }
    event.preventDefault();
    const p = visiblePoint(event);

    if (tool === "text") {
      onAdd(page.id, { type: "text", ...placeAtVisible(p.x, p.y, W, H, R), text: DEFAULT_TEXT, size: textSize, color, bold: false });
      return;
    }

    wrapperRef.current.setPointerCapture(event.pointerId);
    if (tool === "pen" || tool === "highlight") {
      drawRef.current = { kind: tool, points: [visibleToNative(p.x, p.y, W, H, R)] };
    } else if (tool === "rect" || tool === "redact") {
      drawRef.current = { kind: "rect", as: tool, a: p, b: p };
    }
    setDraft({ ...drawRef.current });
  };

  const handlePageMove = (event) => {
    const d = drawRef.current;
    if (!d) return;
    const p = visiblePoint(event);
    if (d.kind === "rect") {
      d.b = p;
    } else {
      const n = visibleToNative(p.x, p.y, W, H, R);
      const last = d.points[d.points.length - 1];
      if (Math.hypot(n.x - last.x, n.y - last.y) < 0.6) return;
      d.points = [...d.points, n];
    }
    setDraft({ ...d });
  };

  const handlePageUp = () => {
    const d = drawRef.current;
    drawRef.current = null;
    setDraft(null);
    if (!d) return;

    if (d.kind === "rect") {
      const x = Math.min(d.a.x, d.b.x);
      const y = Math.min(d.a.y, d.b.y);
      const w = Math.abs(d.a.x - d.b.x);
      const h = Math.abs(d.a.y - d.b.y);
      if (w < 4 || h < 4) return;
      if (d.as === "redact") onAdd(page.id, { type: "redact", ...placeAtVisible(x, y, W, H, R), w, h, mode: redactMode || "blur", strength: redactStrength || 10 });
      else onAdd(page.id, { type: "rect", ...placeAtVisible(x, y, W, H, R), w, h, color, width });
    } else {
      onAdd(page.id, { type: d.kind === "highlight" ? "highlight" : "stroke", points: d.points, color, width });
    }
  };

  /* ---------- ลาก/ปรับขนาดออบเจกต์ ---------- */

  const isSelected = (id) => selectedIds.includes(id);

  // Shift/Cmd/Ctrl+คลิก = เพิ่ม/ถอดจากการเลือก, คลิกชิ้นที่อยู่ในกลุ่มแล้ว = ลากทั้งกลุ่ม
  const beginDrag = (event, obj, mode) => {
    if (!isSelectTool || event.button !== 0) return;
    event.stopPropagation();
    onActivate(page.id);

    let ids = selectedIds;
    if (mode === "move") {
      if (event.shiftKey || event.metaKey || event.ctrlKey) {
        ids = isSelected(obj.id) ? selectedIds.filter((id) => id !== obj.id) : [...selectedIds, obj.id];
        onSelect(ids);
        return; // เปลี่ยนการเลือกอย่างเดียว ไม่เริ่มลาก
      }
      if (!isSelected(obj.id)) {
        ids = [obj.id];
        onSelect(ids);
      }
    } else {
      ids = [obj.id];
      onSelect(ids);
    }

    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      mode, id: obj.id, ids, sx: event.clientX, sy: event.clientY, orig: obj, moved: false,
      origs: page.objects.filter((o) => ids.includes(o.id)),
    };
  };

  const moveDrag = (event) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = (event.clientX - d.sx) / z;
    const dy = (event.clientY - d.sy) / z;
    if (!d.moved) {
      if (Math.abs(dx) + Math.abs(dy) < 2) return;
      d.moved = true;
      onBeginEdit();
    }
    const n = visibleVecToNative(dx, dy, R);

    if (d.mode === "move") {
      const patches = {};
      d.origs.forEach((o) => {
        patches[o.id] = o.points
          ? { points: o.points.map((p) => ({ x: p.x + n.x, y: p.y + n.y })) }
          : { x: o.x + n.x, y: o.y + n.y };
      });
      onUpdateMany(page.id, patches);
      return;
    }

    // ปรับขนาดจากมุมล่างขวาของกล่อง (แกนของออบเจกต์) ตรึงมุมบนซ้าย
    const l = rotateVec(n.x, n.y, -(d.orig.rot || 0));
    if (d.orig.type === "image") {
      const ratio = d.orig.h / d.orig.w;
      const scale = Math.max((d.orig.w + l.x) / d.orig.w, (d.orig.h + l.y) / d.orig.h);
      const w = Math.max(10, d.orig.w * scale);
      onUpdateMany(page.id, { [d.id]: { w, h: w * ratio } });
    } else {
      onUpdateMany(page.id, { [d.id]: { w: Math.max(8, d.orig.w + l.x), h: Math.max(8, d.orig.h + l.y) } });
    }
  };

  const endDrag = () => {
    dragRef.current = null;
  };

  /* ---------- ส่วนแสดงผล ---------- */

  const overlayTransform = {
    0: "none",
    90: `translate(${v.w * z}px, 0) rotate(90deg)`,
    180: `translate(${W * z}px, ${H * z}px) rotate(180deg)`,
    270: `translate(0, ${v.h * z}px) rotate(270deg)`,
  }[R];

  const boxStyle = (o) => ({
    position: "absolute",
    left: o.x * z,
    top: o.y * z,
    transformOrigin: "0 0",
    transform: `rotate(${o.rot || 0}deg)`,
    touchAction: "none",
    userSelect: "none",
    cursor: isSelectTool ? "move" : "default",
    pointerEvents: isSelectTool ? "auto" : "none",
  });

  const selectedOutline = { outline: `1.5px dashed ${SELECT_COLOR}`, outlineOffset: 2 };

  const renderStroke = (o, isDraft) => {
    const hl = o.type === "highlight" || o.kind === "highlight";
    const d = pathOf(o.points);
    const selected = !isDraft && isSelected(o.id);
    const box = selected ? bboxOf(o.points, o.width / 2 + 3) : null;
    return (
      <svg
        key={isDraft ? "draft" : o.id}
        viewBox={`0 0 ${W} ${H}`}
        style={{ position: "absolute", left: 0, top: 0, width: W * z, height: H * z, pointerEvents: "none", overflow: "visible" }}
      >
        <path d={d} fill="none" stroke={o.color} strokeWidth={o.width} strokeOpacity={hl ? 0.35 : 1} strokeLinecap="round" strokeLinejoin="round" />
        {!isDraft && isSelectTool ? (
          <path
            d={d}
            fill="none"
            stroke="transparent"
            strokeWidth={Math.max(o.width, 12 / z)}
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ pointerEvents: "stroke", cursor: "move", touchAction: "none" }}
            onPointerDown={(e) => beginDrag(e, o, "move")}
            onPointerMove={moveDrag}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
          />
        ) : null}
        {box ? (
          <rect x={box.x} y={box.y} width={box.w} height={box.h} fill="none" stroke={SELECT_COLOR} strokeWidth={1.5} strokeDasharray="5 3" vectorEffect="non-scaling-stroke" pointerEvents="none" />
        ) : null}
      </svg>
    );
  };

  const renderHandle = (o) => (
    <div
      onPointerDown={(e) => beginDrag(e, o, "resize")}
      onPointerMove={moveDrag}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      title="ลากเพื่อปรับขนาด"
      style={{
        position: "absolute", right: -7, bottom: -7, width: 13, height: 13, background: "#fff",
        border: `2px solid ${SELECT_COLOR}`, borderRadius: 3, cursor: "nwse-resize", touchAction: "none",
      }}
    />
  );

  const renderObject = (o) => {
    const selected = isSelected(o.id);
    const single = selected && selectedIds.length === 1;
    const handlers = {
      onPointerDown: (e) => beginDrag(e, o, "move"),
      onPointerMove: moveDrag,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
    };

    if (o.type === "stroke" || o.type === "highlight") return renderStroke(o, false);

    if (o.type === "image") {
      return (
        <div key={o.id} {...handlers} style={{ ...boxStyle(o), width: o.w * z, height: o.h * z, ...(selected ? selectedOutline : null) }}>
          <img src={o.src} alt="" draggable={false} style={{ width: "100%", height: "100%", display: "block", pointerEvents: "none", opacity: o.opacity ?? 1 }} />
          {single && isSelectTool ? renderHandle(o) : null}
        </div>
      );
    }

    if (o.type === "redact") {
      return (
        <div
          key={o.id}
          {...handlers}
          style={{
            ...boxStyle(o), width: o.w * z, height: o.h * z, boxSizing: "border-box",
            border: `1.5px dashed ${selected ? SELECT_COLOR : "#f43f5e"}`,
          }}
        >
          {single && isSelectTool ? renderHandle(o) : null}
        </div>
      );
    }

    if (o.type === "rect") {
      const half = (o.width * z) / 2;
      return (
        <div
          key={o.id}
          {...handlers}
          style={{
            ...boxStyle(o), width: o.w * z, height: o.h * z,
            boxShadow: `0 0 0 ${half}px ${o.color}, inset 0 0 0 ${half}px ${o.color}`,
            ...(selected ? { outline: `1.5px dashed ${SELECT_COLOR}`, outlineOffset: half + 3 } : null),
          }}
        >
          {single && isSelectTool ? renderHandle(o) : null}
        </div>
      );
    }

    // text
    return (
      <div
        key={o.id}
        {...handlers}
        style={{
          ...boxStyle(o),
          fontFamily: `${UI_FONT_FAMILY}, Sarabun, Tahoma, sans-serif`,
          fontSize: o.size * z,
          lineHeight: TEXT_LINE_HEIGHT,
          fontWeight: o.bold ? 700 : 400,
          color: o.color,
          whiteSpace: "pre",
          minWidth: o.size * z,
          minHeight: o.size * z * TEXT_LINE_HEIGHT,
          ...(selected ? selectedOutline : null),
        }}
      >
        {o.text}
      </div>
    );
  };

  const rectDraft = draft && draft.kind === "rect"
    ? { x: Math.min(draft.a.x, draft.b.x), y: Math.min(draft.a.y, draft.b.y), w: Math.abs(draft.a.x - draft.b.x), h: Math.abs(draft.a.y - draft.b.y) }
    : null;

  return (
    <div ref={hostRef} data-page-id={page.id} className="d-flex flex-column align-items-center mb-4">
      <div
        ref={wrapperRef}
        className="position-relative bg-white"
        onPointerDown={handlePageDown}
        onPointerMove={handlePageMove}
        onPointerUp={handlePageUp}
        onPointerCancel={handlePageUp}
        style={{
          width: v.w * z,
          height: v.h * z,
          boxShadow: active ? "0 0 0 3px rgba(12,118,229,0.55), 0 10px 28px rgba(12,118,229,0.18)" : "0 6px 18px rgba(34,56,74,0.16)",
          cursor: isSelectTool ? "default" : tool === "text" ? "text" : "crosshair",
          touchAction: isSelectTool ? "auto" : "none",
        }}
      >
        <canvas ref={canvasRef} style={{ width: "100%", height: "100%", display: "block", pointerEvents: "none" }} />
        <div
          style={{
            position: "absolute", left: 0, top: 0, width: W * z, height: H * z,
            transformOrigin: "0 0", transform: overlayTransform, pointerEvents: "none",
          }}
        >
          {page.objects.map(renderObject)}
          {draft && draft.kind !== "rect" ? renderStroke({ points: draft.points, color, width, kind: draft.kind }, true) : null}
        </div>
        {rectDraft ? (
          <div
            style={{
              position: "absolute", left: rectDraft.x * z, top: rectDraft.y * z, width: rectDraft.w * z, height: rectDraft.h * z,
              border: `${Math.max(1, width * z)}px solid ${color}`, boxSizing: "border-box", pointerEvents: "none",
            }}
          />
        ) : null}
      </div>
      {hideLabel ? null : <div className="small text-secondary mt-2">หน้า {index + 1}</div>}
    </div>
  );
}
